import React, { useEffect, useRef, useState } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Input, InputLabel, Tooltip } from '@mui/material';

// Utils
import { EMAIL_REGEX } from '~/utils/constants';
import { validURL } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

// Icons
import {
  ContactInfoCIcon,
  FileUploadCIcon,
  FilterCrossCIcon,
  GeneralPencilCIcon,
  RequestChangesCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

const ACCEPTED_FILE_TYPES = ['png', 'jpg', 'jpeg', 'pdf'];
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES_COUNT = 5;
const COMMENTS_MAX_LENGTH = 1000;
const DESIGN_REQUEST_TYPES = {
  CREATE: 'create_new_design',
  EDIT: 'edit_existing_template',
};
const DEFAULT_DESIGN_REQUEST_TYPE = DESIGN_REQUEST_TYPES.CREATE;
const INITIAL_FORM_VALUES = {
  templateName: '',
  templateId: '',
  productId: '',
  videoUrl: '',
  email: '',
  comments: '',
};

const REQUEST_TYPE_OPTIONS = [
  {
    value: DESIGN_REQUEST_TYPES.CREATE,
    label: 'Create a new design',
    summaryLabel: 'Create New Design',
    icon: RequestChangesCIcon,
  },
  {
    value: DESIGN_REQUEST_TYPES.EDIT,
    label: 'Edit an existing template',
    summaryLabel: 'Edit Existing Template',
    icon: GeneralPencilCIcon,
  },
];

const getInitialFormValues = (defaultEmail = '') => ({
  ...INITIAL_FORM_VALUES,
  email: defaultEmail,
});

const formatFileSize = (size) => {
  if (!size) return '0 KB';

  const sizeInMb = size / (1024 * 1024);
  if (sizeInMb >= 1) {
    return `${sizeInMb.toFixed(2)} MB`;
  }

  return `${Math.max(size / 1024, 1).toFixed(0)} KB`;
};

const hasInvalidFiles = (files) =>
  files.some((file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return !ACCEPTED_FILE_TYPES.includes(extension);
  });

const hasOversizedFiles = (files) => files.some((file) => file.size > MAX_FILE_SIZE_BYTES);

const hasTooManyFiles = (files) => files.length > MAX_FILES_COUNT;

const getFilesValidationError = (files) => {
  if (hasInvalidFiles(files)) {
    return 'Supported formats: png, jpg, jpeg, pdf';
  }

  if (hasTooManyFiles(files)) {
    return `You can upload up to ${MAX_FILES_COUNT} files`;
  }

  if (hasOversizedFiles(files)) {
    return `Maximum file size is ${MAX_FILE_SIZE_MB}MB`;
  }

  return '';
};

const getTooltipContent = () => (
  <Box className="videoTooltipContent">
    <Typography variant="body1">
      Record a video to explain your design ideas clearly. Create a free Loom account to use.
    </Typography>
    <OlcButton
      variant="primary"
      onClick={() => window.open('https://www.loom.com/', '_blank', 'noopener,noreferrer')}
    >
      Use Loom
    </OlcButton>
  </Box>
);

const findOptionById = (options, selected) =>
  options.find((option) => String(option.id ?? option.value) === String(selected));

const getTemplateProduct = (template) =>
  template?.product ||
  template?.Product ||
  template?.object?.product ||
  template?.object?.Product ||
  null;

const getTemplateProductId = (template) => {
  const product = getTemplateProduct(template);

  return (
    template?.productId ??
    template?.product_id ??
    template?.object?.productId ??
    template?.object?.product_id ??
    product?.id ??
    ''
  );
};

const getProductTitle = (product) => {
  if (!product?.productType) {
    return product?.name || '';
  }

  if (product.productType === 'Postcards' && product.paperSize) {
    return `${product.productType} - ${product.paperSize}`;
  }

  if (product.productType === 'Professional Letters') {
    const isWindowedEnvelope = product.isWindowedEnvelope ?? product.windowed;
    const windowedLabel = isWindowedEnvelope ? 'Windowed' : 'Non-Windowed';

    return `${product.productType} - ${windowedLabel}`;
  }

  return product.productType;
};

const HireForm = ({
  openHireForm,
  handleCloseHireForm,
  loading = false,
  onSubmit,
  defaultEmail = '',
  productOptions = [],
  templateOptions = [],
  selectedTemplate = null,
  onSelectedTemplateChange,
  onTemplateSearch,
  templatesLoading = false,
}) => {
  const [openLoomModal, setOpenLoomModal] = useState(false);
  const [step, setStep] = useState(1);
  const [requestType, setRequestType] = useState(DEFAULT_DESIGN_REQUEST_TYPE);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formValues, setFormValues] = useState(() => getInitialFormValues(defaultEmail));
  const [formErrors, setFormErrors] = useState({});
  const selectedRequestTypeOption = findOptionById(REQUEST_TYPE_OPTIONS, requestType);
  const isEditRequest = requestType === DESIGN_REQUEST_TYPES.EDIT;
  const selectedTemplateProductTitle = getProductTitle(getTemplateProduct(selectedTemplate));
  const selectedProductOption = findOptionById(productOptions, formValues.productId);
  const isProductTypeDisabled = productOptions.length === 0 || isEditRequest;

  useEffect(() => {
    if (openHireForm) {
      setFormValues((prev) => ({
        ...prev,
        email: prev.email || defaultEmail,
      }));
      return;
    }

    setSelectedFiles([]);
    setStep(1);
    setRequestType(DEFAULT_DESIGN_REQUEST_TYPE);
    setFormValues(getInitialFormValues(defaultEmail));
    setFormErrors({});
  }, [defaultEmail, openHireForm]);

  const handleOpenLoomModal = () => {
    setOpenLoomModal(true);
  };

  const handleCloseLoomModal = () => {
    setOpenLoomModal(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleTemplateChange = (template) => {
    const templateId = template?.id || '';
    const productId = getTemplateProductId(template);

    onSelectedTemplateChange?.(template || null);
    setFormValues((prev) => ({
      ...prev,
      templateId,
      templateName: template?.object?.title || template?.title || '',
      productId,
    }));
    setFormErrors((prev) => ({
      ...prev,
      templateId: '',
      templateName: '',
      productId: '',
    }));
  };

  const handleRequestTypeChange = (nextRequestType) => {
    if (nextRequestType !== requestType) {
      setFormValues(getInitialFormValues(defaultEmail));
      setSelectedFiles([]);
      onSelectedTemplateChange?.(null);
    }

    setRequestType(nextRequestType);
    setFormErrors({});
  };

  const handleContinue = () => {
    if (!requestType) {
      setFormErrors((prev) => ({
        ...prev,
        requestType: 'Please select a request type',
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      email: prev.email || defaultEmail,
    }));
    setFormErrors({});
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setFormErrors({});
  };

  const handleFiles = (files) => {
    const incomingFiles = Array.from(files || []);

    if (!incomingFiles.length) {
      return;
    }

    const incomingFilesError = getFilesValidationError(incomingFiles);

    if (incomingFilesError) {
      setFormErrors((prev) => ({
        ...prev,
        files: incomingFilesError,
      }));
      return;
    }

    const nextFiles = [...selectedFiles, ...incomingFiles];
    const nextFilesError = getFilesValidationError(nextFiles);

    if (nextFilesError) {
      setFormErrors((prev) => ({
        ...prev,
        files: nextFilesError,
      }));
      return;
    }

    setSelectedFiles(nextFiles);
    setFormErrors((prev) => ({
      ...prev,
      files: '',
    }));
  };

  const handleFileChange = (event) => {
    handleFiles(event.target.files);
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFiles(event.dataTransfer.files);
  };

  const handleFileUploadKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleClearField = (fieldName) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: '',
    }));
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: '',
    }));
  };

  const handleRemoveFile = (fileIndex) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const validateForm = () => {
    const errors = {};
    const templateName = formValues.templateName.trim();
    const templateId = formValues.templateId;
    const productId = formValues.productId;
    const email = formValues.email.trim();
    const comments = formValues.comments.trim();
    const videoUrl = formValues.videoUrl.trim();

    if (isEditRequest) {
      if (!templateId) {
        errors.templateId = 'Existing template is required';
      }
    } else if (!templateName) {
      errors.templateName = 'Template name is required';
    }

    if (!productId) {
      errors.productId = 'Product type is required';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(email)) {
      errors.email = 'Email is not valid';
    }

    if (!comments) {
      errors.comments = 'Comments are required';
    }

    if (videoUrl && !validURL(videoUrl)) {
      errors.videoUrl = MESSAGES.HIRE_DESIGNER.INVALID_URL;
    }

    const filesError = getFilesValidationError(selectedFiles);
    if (filesError) {
      errors.files = filesError;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    const didSubmit = await onSubmit?.({
      ...formValues,
      requestType,
      templateName: formValues.templateName.trim(),
      templateId: isEditRequest ? formValues.templateId : '',
      productId: formValues.productId,
      email: formValues.email.trim(),
      comments: formValues.comments.trim(),
      url: formValues.videoUrl.trim(),
      files: selectedFiles,
    });

    if (didSubmit) {
      setSelectedFiles([]);
      setStep(1);
      setRequestType(DEFAULT_DESIGN_REQUEST_TYPE);
      setFormValues(getInitialFormValues(defaultEmail));
      setFormErrors({});
    }
  };

  const renderProductValue = (selected) => {
    if (!selected) {
      return 'Product Type';
    }

    return selectedProductOption?.title || selectedTemplateProductTitle || 'Product Type';
  };

  return (
    <OlcFormModal
      open={openHireForm}
      onClose={handleCloseHireForm}
      title="Request Design Help"
      subtitle={`Step ${step} of 2`}
      className={`hireFormModal ${step === 1 ? 'requestTypeStep' : 'requestDetailsStep'}`}
      footer={
        <>
          <OlcButton
            variant="secondary"
            onClick={step === 1 ? handleCloseHireForm : handleBack}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </OlcButton>
          <OlcButton
            variant="primary"
            disabled={loading}
            type="button"
            onClick={step === 1 ? handleContinue : handleSubmit}
          >
            {step === 1 ? (
              'Continue'
            ) : loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              'Submit Design Request'
            )}
          </OlcButton>
        </>
      }
    >
      {step === 1 ? (
        <Box className="requestTypeContent">
            <Box className={`inputWrapper ${formErrors.requestType ? 'errorField' : ''}`}>
              <InputLabel>
                What do you need help with?<span className="requiredMark">*</span>
              </InputLabel>
              <Box className="requestTypeOptions">
                {REQUEST_TYPE_OPTIONS.map((option) => {
                  const isSelected = requestType === option.value;

                  return (
                    <Box
                      key={option.value}
                      className={`requestTypeOption ${isSelected ? 'selected' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRequestTypeChange(option.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRequestTypeChange(option.value);
                        }
                      }}
                    >
                      <Box className="requestTypeLabel">
                        <img src={option.icon} alt="" />
                        <Typography>{option.label}</Typography>
                      </Box>
                      <Box className="requestTypeRadio" />
                    </Box>
                  );
                })}
              </Box>
              {formErrors.requestType && (
                <Typography className="errorText">{formErrors.requestType}</Typography>
              )}
            </Box>
            <Typography className="selectionHint">
              We'll tailor the request form based on your selection.
            </Typography>
        </Box>
      ) : (
        <Box component="form" id="hireDesignerForm" onSubmit={handleSubmit}>
            <Box className="requestSummary">
              <img src={selectedRequestTypeOption?.icon} alt="" />
              <Typography>
                Request Type: <span>{selectedRequestTypeOption?.summaryLabel}</span>
              </Typography>
            </Box>
            {isEditRequest ? (
              <Box className={`inputWrapper ${formErrors.templateId ? 'errorField' : ''}`}>
                <InputLabel>
                  Existing Template<span className="requiredMark">*</span>
                </InputLabel>
                <OlcDropdownSearch
                  options={templateOptions}
                  selectedValue={selectedTemplate}
                  setSelectedValue={handleTemplateChange}
                  clearable={true}
                  disabled={templatesLoading && templateOptions.length === 0}
                  placeHolderText="Select existing template"
                  noOptionText="No options found"
                  component="multipleIds"
                  searchAutocomplete
                  searchFieldPlaceholderText="Template"
                  onSearchChange={onTemplateSearch}
                  searchLoading={templatesLoading}
                  showCreateOption={false}
                  className={`hireTemplateSelect ${
                    selectedTemplate ? 'activeState' : 'inactiveState'
                  }`}
                  activeState={true}
                />
                {formErrors.templateId && (
                  <Typography className="errorText">{formErrors.templateId}</Typography>
                )}
              </Box>
            ) : (
              <Box className={`inputWrapper ${formErrors.templateName ? 'errorField' : ''}`}>
                <InputLabel>
                  Template Name<span className="requiredMark">*</span>
                </InputLabel>
                <Input
                  type="text"
                  name="templateName"
                  value={formValues.templateName}
                  onChange={handleInputChange}
                  placeholder="Enter template name"
                />
                {formErrors.templateName && (
                  <Typography className="errorText">{formErrors.templateName}</Typography>
                )}
              </Box>
            )}
            <Box className={`inputWrapper ${formErrors.productId ? 'errorField' : ''}`}>
              <InputLabel>
                Product Type<span className="requiredMark">*</span>
              </InputLabel>
              <Select
                name="productId"
                value={formValues.productId}
                onChange={handleInputChange}
                displayEmpty
                className="productTypeSelect"
                disabled={isProductTypeDisabled}
                renderValue={renderProductValue}
                IconComponent={(props) => (
                  <OlcDropdownChevron
                    {...props}
                    disabled={isProductTypeDisabled}
                    style={{
                      ...props.style,
                      marginTop: '-2.5px',
                      right: '20px',
                      top: '50%',
                    }}
                  />
                )}
              >
                {formValues.productId && !selectedProductOption && selectedTemplateProductTitle && (
                  <MenuItem value={formValues.productId} sx={{ display: 'none' }}>
                    {selectedTemplateProductTitle}
                  </MenuItem>
                )}
                {productOptions.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.title}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.productId && (
                <Typography className="errorText">{formErrors.productId}</Typography>
              )}
            </Box>
            <Box className={`inputWrapper ${formErrors.files ? 'errorField' : ''}`}>
              <Box
                component="label"
                htmlFor="hireDesignerFileInput"
                className="fileUploadArea"
                role="button"
                tabIndex={0}
                aria-label="Upload design reference files"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onKeyDown={handleFileUploadKeyDown}
              >
                <input
                  ref={fileInputRef}
                  id="hireDesignerFileInput"
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  multiple
                  onChange={handleFileChange}
                />
                <Box className="uploadIcon">
                  <img src={FileUploadCIcon} alt="icon" />
                </Box>
                <Typography className="uploadText">
                  <span className="uploadLink">Upload a file</span> or drag and drop here
                </Typography>
                <Typography className="supportedFormats">
                  Supported formats: png, jpg, jpeg, pdf. Maximum 5 files, 50MB each
                </Typography>
              </Box>
              {formErrors.files && (
                <Typography className="errorText">{formErrors.files}</Typography>
              )}
              {selectedFiles.length > 0 && (
                <Box className="selectedFilesWrapper">
                  {selectedFiles.map((file, index) => (
                    <Box className="selectedFile" key={`${file.name}-${file.size}-${index}`}>
                      <Typography>{file.name}</Typography>
                      <Box className="selectedFileActions">
                        <Typography component="span">{formatFileSize(file.size)}</Typography>
                        <IconButton
                          className="removeFileBtn"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <img src={FilterCrossCIcon} alt="" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box className={`inputWrapper ${formErrors.videoUrl ? 'errorField' : ''}`}>
              <InputLabel className="videoUrlLabel">
                Video URL
                <Tooltip
                  title={getTooltipContent()}
                  placement="bottom-start"
                  open={openLoomModal}
                  onOpen={handleOpenLoomModal}
                  onClose={handleCloseLoomModal}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                        padding: '0 !important',
                        maxWidth: '394px !important',
                        margin: '0 !important',
                      },
                    },
                  }}
                >
                  <span onClick={handleOpenLoomModal}>
                    <img src={ContactInfoCIcon} alt="icon" />
                  </span>
                </Tooltip>
              </InputLabel>
              <Input
                type="text"
                name="videoUrl"
                value={formValues.videoUrl}
                onChange={handleInputChange}
                placeholder="https://www.loom.com/share/"
              />
              {formValues.videoUrl && (
                <IconButton
                  className="clearInputBtn"
                  aria-label="clear video URL"
                  onClick={() => handleClearField('videoUrl')}
                >
                  <img src={FilterCrossCIcon} alt="clear" />
                </IconButton>
              )}
              {formErrors.videoUrl && (
                <Typography className="errorText">{formErrors.videoUrl}</Typography>
              )}
            </Box>
            <Box className={`inputWrapper ${formErrors.email ? 'errorField' : ''}`}>
              <InputLabel>
                Email<span className="requiredMark">*</span>
              </InputLabel>
              <Input
                type="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                placeholder="Email"
              />
              {formValues.email && (
                <IconButton
                  className="clearInputBtn"
                  aria-label="clear email"
                  onClick={() => handleClearField('email')}
                >
                  <img src={FilterCrossCIcon} alt="clear" />
                </IconButton>
              )}
              {formErrors.email && (
                <Typography className="errorText">{formErrors.email}</Typography>
              )}
            </Box>
            <Box className={`inputWrapper ${formErrors.comments ? 'errorField' : ''}`}>
              <InputLabel>
                {isEditRequest ? 'What changes do you need?' : 'Design Details'}
                <span className="requiredMark">*</span>
              </InputLabel>
              <Input
                multiline
                rows={3}
                type="text"
                name="comments"
                value={formValues.comments}
                onChange={handleInputChange}
                placeholder={
                  isEditRequest
                    ? "Describe the edits or updates you'd like us to make..."
                    : 'Describe the new design you need, including copy, images, style, colors, and instructions...'
                }
                inputProps={{ maxLength: COMMENTS_MAX_LENGTH }}
              />
              <Typography className="characterCount">
                {formValues.comments.length}/{COMMENTS_MAX_LENGTH}
              </Typography>
              {formErrors.comments && (
                <Typography className="errorText">{formErrors.comments}</Typography>
              )}
            </Box>
            <Box className="designCreditNote">
              <img src={ContactInfoCIcon} alt="" />
              <Typography>Submitting this request will use 1 design credit.</Typography>
            </Box>
        </Box>
      )}
    </OlcFormModal>
  );
};

export default HireForm;
export { DESIGN_REQUEST_TYPES };
